
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Eye, Calendar, Star, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FullReview } from '@/types/review';

const PublishedReviewsSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: publishedReviews, isLoading, error } = useQuery({
    queryKey: ['published-reviews', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          papers (
            id,
            current_version,
            paper_versions (
              title,
              abstract
            )
          )
        `)
        .eq('reviewer_id', user.id)
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id
  });

  const handleViewPaper = (paperId: string) => {
    navigate(`/paper/${paperId}`);
  };

  const handleViewReview = (paperId: string, reviewId: string) => {
    navigate(`/paper/${paperId}#review-${reviewId}`);
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'accept':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'minor_revisions':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'major_revisions':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'reject':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatRecommendation = (recommendation: string) => {
    return recommendation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse border-gray-100">
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-red-700 font-medium">Error loading published reviews</p>
          <p className="text-red-600 text-sm mt-1">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  if (!publishedReviews?.length) {
    return (
      <div className="text-center py-16">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 max-w-md mx-auto">
          <div className="p-3 bg-blue-100 rounded-full w-fit mx-auto mb-4">
            <Star className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-blue-900 mb-2">No published reviews</h3>
          <p className="text-blue-700 mb-4">
            You haven't published any reviews yet. Start reviewing papers to see your published reviews here.
          </p>
          <Button 
            onClick={() => navigate('/explore')} 
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Explore Papers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {publishedReviews.map((review) => {
        const paper = review.papers;
        const currentVersion = paper?.paper_versions?.[0];

        return (
          <Card key={review.id} className="border-l-4 border-l-blue-400 hover:shadow-md transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle 
                    className="text-xl mb-3 line-clamp-2 text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() => handleViewPaper(paper?.id)}
                  >
                    {currentVersion?.title || 'Untitled Paper'}
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>Published: {new Date(review.published_at).toLocaleDateString()}</span>
                    </div>
                    {review.overall_rating && (
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4" />
                        <span>Rating: {review.overall_rating.toFixed(1)}/10</span>
                      </div>
                    )}
                  </div>
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
                  Published
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="py-4">
              <p className="text-gray-700 line-clamp-3 mb-4 leading-relaxed">
                {currentVersion?.abstract || 'No abstract available'}
              </p>
              
              <div className="flex items-center gap-4 mb-4">
                {review.recommendation && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Recommendation:</span>
                    <Badge 
                      variant="outline" 
                      className={`capitalize font-medium ${getRecommendationColor(review.recommendation)}`}
                    >
                      {formatRecommendation(review.recommendation)}
                    </Badge>
                  </div>
                )}
              </div>

              {review.summary && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700 line-clamp-2">
                    <span className="font-medium">Summary: </span>
                    {review.summary}
                  </p>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex gap-2 pt-4 bg-gray-50/50 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewPaper(paper?.id)}
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Paper
              </Button>
              
              <Button
                onClick={() => handleViewReview(paper?.id, review.id)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Review
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};

export default PublishedReviewsSection;
